import { Component, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { environment } from '../environments/environment';
import { DataserviceService } from '../dataservice.service';
import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';
import { PinModalComponent } from './pin-modal/pin-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AreaModalComponent } from './area-modal/area-modal.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { AppComponent } from '../app.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { MduListComponent } from './mdu-list/mdu-list.component';

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

type LoadedArea = {
  sw: [number, number]; // [lat, lng]
  ne: [number, number]; // [lat, lng]
};

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: './map.component.css'
})
export class MapComponent implements OnDestroy {
  @ViewChild('pieChartAdd', { static: false }) pieChartAdd!: ElementRef;

  map!: mapboxgl.Map;
  draw!: MapboxDraw;

  currentUser: any;

  tracking!: any;

  areasRes: any[] = [];
  areas: any;
  pins: any;
  dispositions: any[] = [];
  users: any[] = []
  mapStyle = 'mapbox://styles/mapbox/light-v10'
  styles = [
    { name: 'Streets', url: 'mapbox://styles/mapbox/streets-v11', icon: 'car', theme: 'fill' },
    { name: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9', icon: 'global', theme: 'outline' },
    { name: 'Outdoors', url: 'mapbox://styles/mapbox/outdoors-v11', icon: 'picture', theme: 'fill' },
    { name: 'Light', url: 'mapbox://styles/mapbox/light-v10', icon: 'bulb', theme: 'fill' },
    { name: 'Dark', url: 'mapbox://styles/mapbox/dark-v10', icon: 'moon', theme: 'fill' }
  ];
  drawerTitle: any;
  newPolygon: any;
  pinsInside: any

  pinLoading: boolean = false;
  liveTracking: boolean = false;
  areaSelection: boolean = false;
  showStyleBar: boolean = false;
  markerMode: boolean = false;
  drawingMode: boolean = false;
  spinner: boolean = false;
  showDrawer: boolean = false;
  showCreateAreaModal: boolean = false;
  showFilterDrawer: boolean = false;

  newAreaForm: FormGroup;
  filterForm: FormGroup;

  zoomLevel = 6;
  loadedAreas: LoadedArea[] = [];
  movePins: boolean = false;

  movingPinId: any;
  startLatandLng: any;
  lastLatandLng: any;

  companies: any = [];

  notYouPeenz: boolean = false;
  newAreaStats: any = [];
  selectedDispos: any = [];
  selectedDisposCount = 0;
  subSegment = 'list'

  constructor(
    private data: DataserviceService,
    private modal: NzModalService,
    private app: AppComponent,
    private message: NzMessageService
  ) {

    this.handlePinClick = this.handlePinClick.bind(this);
    this.handleAreaClick = this.handleAreaClick.bind(this);

    this.newAreaForm = new FormGroup({
      users: new FormControl(null),
      color: new FormControl(null)
    });

    this.filterForm = new FormGroup({
      'provider': new FormControl(null, [Validators.required]),
      'disposition': new FormControl([]),
      'user': new FormControl(null),
      'company': new FormControl(null)
    })

  }

  ngOnInit() {
    this.initMap();
    
    window.addEventListener('openMapDrawer', () => {
      if(this.filterForm.get('disposition')?.value?.length == 0) {
        this.filterForm.get('disposition')?.setValue(this.dispositions?.map((d: any) => d?.id))
      }
      this.showFilterDrawer = !this.showFilterDrawer
    });
  }

  ngAfterViewInit() {
    const mapContainer = document.getElementById('map');
    if (mapContainer) {
      const resizeObserver = new ResizeObserver(() => {
        if (this.map && this.map.loaded()) {
          this.map.resize();
        }
      });
      resizeObserver.observe(mapContainer);
    }
  }

  setDefault() {
    localStorage.setItem('provider', this.filterForm.get('provider')?.value);
     this.message.success('Default provider set!', { nzDuration: 1250 });
  }

  updateNotYouPeenz(): void {
    const filteredUser = this.filterForm.get('user')?.value;
    this.notYouPeenz = !filteredUser || this.compareUser(filteredUser, this.currentUser);
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }

    this.drawingMode = false;
    this.areaSelection = false;
    this.liveTracking = false;

  }

  turnOffSpinner() {
    setTimeout(() => {
      this.spinner = false;
    }, 2000)
  }

  hasPermission(permission: string): boolean {
    return this.app.hasPermission(permission);
  }

  setParams() {
    let params = new HttpParams()

    if (this.hasPermission('view_distributor_users') || this.hasPermission('view_all_users')) {
      params = params.set('distributor_id', this.currentUser?.distributor_id);
      params = params.set('provider', this.filterForm.get('provider')?.value);
    }
    else if (this.hasPermission('view_company_users')) {
      params = params.set('company_id', this.filterForm.get('company')?.value || this.currentUser?.company_id);
      params = params.set('provider', this.filterForm.get('provider')?.value);
    }
    else if (this.currentUser?.viewable_groups?.length > 0) {
      params = params.set('viewable_groups', this.currentUser.viewable_groups);
      params = params.set('provider', this.filterForm.get('provider')?.value);
    }
    else {
      params = params.set('my_id', this.currentUser.id);
      params = params.set('provider', this.filterForm.get('provider')?.value);
    }

    return params;
  }

  getCompanies() {
    this.data.getData(`company/companies-for-map/${this.filterForm.get('provider')?.value}`)
      .subscribe({
        next: (res: any) => {
          const companies = res?.data || [];
  
          this.companies = companies.sort((a: any, b: any) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
  
            if (nameA < nameB) {
              return -1;
            }
  
            if (nameA > nameB) {
              return 1;
            }
  
            return 0;
          });
        }
      });
  }

  async initMap() {
    this.updateNotYouPeenz();
    this.pinLoading = true;
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const prov = localStorage.getItem('provider') || null
    if (this.currentUser?.providers && this.currentUser.providers.length > 0) {
      this.currentUser.providers = this.currentUser.providers.sort((a: any, b: any) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      
    }

    if(prov) {
      this.filterForm.get('provider')?.setValue(prov);
    }else {
      this.filterForm.get('provider')?.setValue(this.currentUser.providers[0].id);
    }
    const myLoc: [number, number] = await this.getMyLocation();

    (mapboxgl as any).accessToken = environment.mapbox.accessToken;
    this.map = new mapboxgl.Map({
      container: 'map',
      style: this.mapStyle,
      center: myLoc,
      zoom: this.zoomLevel,
      logoPosition: 'bottom-left',
    });

    this.spinner = true;

    this.map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');

    this.tracking = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
      showAccuracyCircle: true
    });

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {}
    });

    // this.getUsers();
    this.getAreas();
    // this.setLiveTracking();

    const geocoder = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      autocomplete: true,
    });

    this.map.addControl(geocoder, "top-left");

    this.map.on('click', (e) => {
      if (this.markerMode) {

        const lng = e.lngLat.lng;
        const lat = e.lngLat.lat;

        const insideArea = this.areas.find((area: any) => {
          return booleanPointInPolygon(point([lng, lat]), area);
        });

        // this.addPointToMap(lng, lat, insideArea);
        this.showConfirm(lng, lat, insideArea)
      }
    });

    this.map.on('mouseenter', 'my-polygon-fill', () => {
      if (this.areaSelection) {
        this.map.getCanvas().style.cursor = 'pointer';
      }
    });

    this.map.on('mouseleave', 'my-polygon-fill', () => {
      if (this.areaSelection) {
        this.map.getCanvas().style.cursor = '';
      }

      this.map.on('mouseenter', 'points', () => {
        if (!this.areaSelection) {
          this.map.getCanvas().style.cursor = 'pointer';
        }
      });

      this.map.on('mouseleave', 'points', () => {
        if (!this.areaSelection) {
          this.map.getCanvas().style.cursor = '';
        }
      });
    });

    this.turnOffSpinner();
  }

  showConfirm(lng: any, lat: any, insideArea: any): void {
    this.modal.confirm({
      nzTitle: '<i>Building Type</i>',
      nzContent: '<b>Is this a multi-unit building? Select "Ok" for Apartments, Condos etc.</b>',
      nzOnOk: () => {
        this.addPointToMap(lng, lat, insideArea, true)
      },
      nzOnCancel: () => {
        this.addPointToMap(lng, lat, insideArea, false)
      }
    });
  }

  setClickedFilterDispo(disp: any) {
    if(this.filterForm.get('disposition')?.value?.includes(disp?.id)) {
      const index = this.filterForm.get('disposition')?.value?.indexOf(disp?.id)
      this.filterForm.get('disposition')?.value?.splice(index, 1);
    }else {
      this.filterForm.get('disposition')?.value?.push(disp?.id)
    }
  }

  saveLoadedViewport(bounds: mapboxgl.LngLatBounds) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    this.loadedAreas.push({
      sw: [sw.lat, sw.lng],
      ne: [ne.lat, ne.lng]
    });
  }

  isViewportLoaded(bounds: mapboxgl.LngLatBounds) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    return this.loadedAreas.some((area: any) =>
      sw.lat >= area.sw[0] &&
      sw.lng >= area.sw[1] &&
      ne.lat <= area.ne[0] &&
      ne.lng <= area.ne[1]
    );
  }

  getUsers() {
    let params = this.setParams();
  
    this.data.getData(`users/users-for-map`, { params })
      .subscribe({
        next: (res: any) => {
          const users = res || [];
  
          this.users = users.sort((a: any, b: any) => {
            const fullNameA = ((a.first_name || '') + ' ' + (a.last_name || '')).toLowerCase();
            const fullNameB = ((b.first_name || '') + ' ' + (b.last_name || '')).toLowerCase();
  
            if (fullNameA < fullNameB) {
              return -1;
            }
  
            if (fullNameA > fullNameB) {
              return 1;
            }
  
            return 0;
          });
        },
      });
  }

  setMapStyle(styleUrl: string) {
    this.spinner = true;
    this.map.setStyle(styleUrl);

    this.map.once('styledata', () => {
      this.loadAreas(this.areasRes)
      this.loadPins(this.pins);
    });

    this.mapStyle = styleUrl;
    this.showStyleBar = false;
    this.turnOffSpinner();
  }

  getDispositions() {
    this.data.getData(`disposition/dispositions-by-provider/${this.filterForm.get('provider')?.value}`)
      .subscribe({
        next: (res: any) => {
          if(JSON.stringify(this.dispositions) != JSON.stringify(res?.data)) {
            this.filterForm.get('disposition')?.setValue([])
          }
          this.dispositions = res.data;
        },
        error: (err: any) => {
          setTimeout(() => {

          }, 1000);
        }
      })
  }

  addPointToMap(lng: any, lat: any, area: any, repSelectedMdu: any) {
    this.data.getAddress(lat, lng)
      .subscribe({
        next: async (res: any) => {
          this.data.getData(`pins/check-for-mdu?lng=${res.features[0]?.geometry?.coordinates[0].toFixed(5)}&lat=${res.features[0]?.geometry?.coordinates[1].toFixed(5)}&provider=${this.filterForm.get('provider')?.value}`)
            .subscribe({
              next: (mduRes: any) => {

                const isMdu = !!(repSelectedMdu || mduRes?.data);

                const modal = this.modal.create({
                  nzContent: PinModalComponent,
                  nzClosable: false,
                  nzMaskClosable: false,
                  nzFooter: null,
                  nzData: {
                    address: res?.features?.[0]?.properties?.full_address,
                    coords: {
                      latitude: res?.features?.[0]?.properties?.coordinates.latitude,
                      longitude: res?.features?.[0]?.properties?.coordinates.longitude,
                    },
                    dispositions: this.dispositions,
                    area: area?.properties?.areaId || null,
                    provider: this.filterForm.get('provider')?.value,
                    mdu: isMdu
                  }
                })

              modal.afterClose
                .subscribe((res: any) => {

                  if (res) {
                    const id = this.message.loading('Dropping new pin..', { nzDuration: 0 }).messageId;

                    const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;
                    const currentData = structuredClone(source['_data']) as GeoJSON.FeatureCollection;

                    const newFeature: GeoJSON.Feature = {
                      type: 'Feature',
                      geometry: {
                        type: 'Point',
                        coordinates: [lng, lat]
                      },
                      properties: {
                        id: res.res,
                        color: res.pin || '#888888'
                      }
                    };

                    const obj = {
                      id: res.res,
                      latitude: lat,
                      longitude: lng,
                      color: res.pin || '#888888'
                    }

                    this.pins.push(obj)

                    currentData.features.push(newFeature);

                    source.setData(currentData);

                    setTimeout(() => {
                      this.message.remove(id)
                    }, 1200)
                  }
                })
              }
            })
        },
        error: (err: any) => {
          console.log(err)

        }
      })
  }

  showStyles() {
    if (this.showStyleBar) {
      this.showStyleBar = false;
    } else {
      this.showStyleBar = true
    }
    if (this.movePins) {
      this.enableMapGestures();
    }
    this.movePins = false;
  }

  enableMapGestures() {
    this.map.dragPan.enable();
    this.map.scrollZoom.enable();
    this.map.doubleClickZoom.enable();
    this.map.touchZoomRotate.enable();
    this.map.keyboard.enable();
  }

  setMoveMode() {
    this.movePins = !this.movePins;
    if (this.movePins) {

      // this.toasty('Pin moving mode');
      this.map.dragPan.disable();
      this.map.scrollZoom.disable();
      this.map.doubleClickZoom.disable();
      this.map.touchZoomRotate.disable();
      this.map.keyboard.disable();

    } else {
      this.enableMapGestures();
    }
  }

  getAreas() {
    this.updateNotYouPeenz();
    this.loadedAreas = [];
    const userParam = encodeURIComponent(JSON.stringify(this.filterForm.get('user')?.value));
    const companyParam = encodeURIComponent(JSON.stringify(this.filterForm.get('company')?.value));
    this.data.getData(`pins/my-areas/${this.filterForm.get('provider')?.value}?user=${userParam}&company=${companyParam}`)
      .subscribe({
        next: (res: any) => {
          this.getDispositions();
          this.getUsers();
          this.areasRes = res;
          this.loadAreas(res);
          this.getPins();
          this.getCompanies();
        }
      })
  }

  loadAreas(data: any) {
    if (this.map.getSource('my-polygon')) {
      if (this.map.getLayer('my-polygon-fill')) {
        this.map.removeLayer('my-polygon-fill');
      }
      if (this.map.getLayer('my-polygon-outline')) {
        this.map.removeLayer('my-polygon-outline');
      }
      this.map.removeSource('my-polygon');
    }

    const polygonFeatures = data.map((area: any, index: number) => {
      let parsedCoords = JSON.parse(area.coords);

      parsedCoords = parsedCoords.map((coord: any) => {
        if (Array.isArray(coord) && coord.length === 2) {
          return coord;
        } else if (coord && typeof coord === 'object' && 'lat' in coord && 'lng' in coord) {
          return [coord.lng, coord.lat];
        } else {
          console.warn('Invalid coordinate:', coord);
          return null;
        }
      }).filter(Boolean)

      if (
        parsedCoords.length &&
        (parsedCoords[0][0] !== parsedCoords.at(-1)[0] ||
          parsedCoords[0][1] !== parsedCoords.at(-1)[1])
      ) {
        parsedCoords.push(parsedCoords[0]);
      }

      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [parsedCoords]
        },
        properties: {
          id: index,
          color: area.color || '#000',
          areaId: area.id
        }
      };
    });

    const polygonGeoJSON = {
      type: 'FeatureCollection',
      features: polygonFeatures
    } as GeoJSON.FeatureCollection;

    this.map.addSource('my-polygon', {
      type: 'geojson',
      data: polygonGeoJSON
    });

    this.map.addLayer({
      id: 'my-polygon-fill',
      type: 'fill',
      source: 'my-polygon',
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.3
      }
    });

    this.map.addLayer({
      id: 'my-polygon-outline',
      type: 'line',
      source: 'my-polygon',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2
      }
    });

    this.areas = polygonFeatures;
    this.areaClick()


  }

  async getPins() {
    const dispos = this.dispositions?.length === this.filterForm.get('disposition')?.value ? null : this.filterForm.get('disposition')?.value;

    const userParam = encodeURIComponent(JSON.stringify(this.filterForm.get('user')?.value));
    const companyParam = encodeURIComponent(JSON.stringify(this.filterForm.get('company')?.value));
    const dispoParam = encodeURIComponent(JSON.stringify(dispos));
    this.data.getData(`pins/my-pins/${this.filterForm.get('provider')?.value}?user=${userParam}&company=${companyParam}&dispos=${dispoParam}`)
      .subscribe({
        next: (res: any) => {
          this.pins = res;
          this.loadPins(res);
        }
      })
  }

  async getPinsByBounds(bounds: any) {
    const dispos = this.dispositions?.length === this.filterForm.get('disposition')?.value ? null : this.filterForm.get('disposition')?.value;

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const userParam = encodeURIComponent(JSON.stringify(this.filterForm.get('user')?.value));
    const companyParam = encodeURIComponent(JSON.stringify(this.filterForm.get('company')?.value));
    const dispoParam = encodeURIComponent(JSON.stringify(dispos));
    this.data.getData(`pins/my-pins-by-bounds/${this.filterForm.get('provider')?.value}?swLat=${sw.lat}&swLng=${sw.lng}&neLat=${ne.lat}&neLng=${ne.lng}&user=${userParam}&company=${companyParam}&dispos=${dispoParam}`)
      .subscribe({
        next: (res: any) => {
          const existingIds = new Set(this.pins.map((p: any) => p.id));
          const pinsToAdd = res?.filter((p: any) => !existingIds.has(p.id));
          this.pins.push(...pinsToAdd);
          this.loadBoundsPins(res);
          this.pinLoading = false;
        }
      })
  }

  async loadBoundsPins(data: any) {
    const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;

    if (source) {
      const bounds = this.map.getBounds();
      const currentData = source._data as GeoJSON.FeatureCollection<GeoJSON.Point> || { type: 'FeatureCollection', features: [] };
      let cleanFeatures = currentData.features || [];

      if (bounds) {
        cleanFeatures = currentData.features.filter((f: any) => {
    
          if (!f.geometry || !f.geometry.coordinates) return true;

          const [lng, lat] = f.geometry.coordinates;

          const isInside = (
            lng >= bounds.getWest() && 
            lng <= bounds.getEast() && 
            lat >= bounds.getSouth() && 
            lat <= bounds.getNorth()
          );

          return !(f.properties?.guidePin && isInside);
        });
      }

      const newFeatures = data.map((p: any) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
        properties: {
          color: p.color?.trim() ? p.color : '#888888',
          id: p.id,
          disposition_id: p?.disposition_id,
          mdu: p?.mdu,
          coordinates: [p.longitude, p.latitude],
          pin_count: 1
        }
      }));

      // Optional: avoid duplicates
      const existingIds = new Set(cleanFeatures.map((f: any) => f.properties?.id));
      const featuresToAdd = newFeatures.filter((f: any) => !existingIds.has(f.properties?.id));

      source.setData({
        type: 'FeatureCollection',
        features: [...cleanFeatures, ...featuresToAdd]
      });

    } else {
      // First time creating the source
      const geojson = {
        type: 'FeatureCollection',
        features: data.map((p: any) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
          properties: {
            color: p.color?.trim() ? p.color : '#888888',
            id: p.id,
            disposition_id: p?.disposition_id
          }
        }))
      } as GeoJSON.FeatureCollection;

      this.map.addSource('locations', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 10,
        clusterRadius: 30
      });

      // Clusters layer
      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'locations',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': ['step', ['get', 'point_count'], 10, 100, 15, 750, 20],
          'circle-color': '#000000',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1
        }
      });

      // Individual points layer
      this.map.addLayer({
        id: 'points',
        type: 'circle',
        source: 'locations',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 6,   // at zoom 5 -> radius 6px
            12, 7,  // at zoom 12 -> radius 7px
            15, 9,  // at zoom 15 -> radius 9px
            17, 12, // at zoom 17 -> radius 12px
            18, 14 // at zoom 18 -> radius 14px
          ],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1
        }
      });

      // this.map.addLayer({
      //   id: 'point-labels',
      //   type: 'symbol',
      //   source: 'locations',
      //   filter: ['!', ['has', 'point_count']], // only unclustered points
      //   layout: {
      //     'text-field': 'X',                // text to show
      //     'text-font': ['Open Sans Bold'],  // Mapbox-supported font
      //     'text-size': 12,
      //     'text-anchor': 'center',          // centers text on circle
      //     'text-allow-overlap': true
      //   },
      //   paint: {
      //     'text-color': '#ffffff'           // contrasting color over circle
      //   }
      // }, 'points');
    }

  }

  async loadPins(data: any) {

    if (this.map.getSource('locations')) {
      if (this.map.getLayer('clusters')) {
        this.map.removeLayer('clusters');
      }
      if (this.map.getLayer('points')) {
        this.map.removeLayer('points');
      }
      this.map.removeSource('locations');
    }

    const geojson = {
      type: 'FeatureCollection',
      features: data.map((p: any) =>
      ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [p.longitude, p.latitude]
        },
        properties: {
          color: p.color?.trim() ? p.color : '#888888',
          id: p.id,
          disposition_id: p?.disposition_id,
          mdu: p?.mdu,
          pin_count: p.pin_count ? Number(p.pin_count) : 1,
          guidePin: true
        }
      }))
    } as GeoJSON.FeatureCollection;


    this.map.addSource('locations', {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 8,
      clusterProperties: {
        'sum_count': ['+', ['get', 'pin_count']]
      }
    });

    this.map.addLayer({
      id: 'points',
      type: 'circle',
      source: 'locations',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          5, 6,   // at zoom 5 -> radius 6px
          12, 7,  // at zoom 12 -> radius 7px
          15, 9,  // at zoom 15 -> radius 9px
          17, 12, // at zoom 17 -> radius 12px
          18, 14 // at zoom 18 -> radius 14px
        ],
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1
      }
    });

    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'locations',
      filter: ['has', 'point_count'], 
      paint: {
        'circle-radius': [
          'interpolate',
          ['exponential', 1.5],
          ['get', 'sum_count'],
          1, 25,          // Smallest merged cluster starts at 15px
          100, 25,        // 100 pins = 25px
          1000, 40,       // 1,000 pins = 40px
          10000, 65       // 10,000 pins = 65px (Massive)
        ],
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'sum_count'],
          1, '#51bbd6',
          500, '#f1f075',
          2000, '#f28cb1',
          5000, '#e55e5e'
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-opacity': 1
      }
    });

    this.map.addLayer({
      id: 'cluster-count-labels',
      type: 'symbol',
      source: 'locations',
      maxzoom: 9.5, // Match the maxzoom of your clusters
      layout: {
        // Logic: If it has 'point_count' (is a cluster), show 'sum_count'.
        // Otherwise, show the original 'pin_count'.
        'text-field': [
          'case',
          ['has', 'point_count'], 
          ['to-string', ['get', 'sum_count']], 
          ['to-string', ['get', 'pin_count']]
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          3, 10,   // Smaller text when zoomed out
          10, 14   // Larger text when zoomed in
        ],
        'text-allow-overlap': true,    // Force show even if crowded
        'text-ignore-placement': true, // Don't let other map labels hide our counts
        'text-justify': 'center'
      },
      paint: {
        'text-color': '#000000', // Black text pops best on the cyan/yellow/pink circles
        'text-halo-color': '#ffffff',
        'text-halo-width': 1
      }
    });

    // this.map.addLayer({
    //   id: 'point-labels',
    //   type: 'symbol',
    //   source: 'locations',
    //   filter: ['!', ['has', 'point_count']],
    //   layout: {
    //     'text-field': 'X',
    //     'text-font': ['Open Sans Bold'],
    //     'text-size': 12,
    //     'text-anchor': 'center',
    //     'text-allow-overlap': true
    //   },
    //   paint: {
    //     'text-color': '#ffffff'
    //   }
    // });


    this.pinClick();
    setTimeout(() => {
      this.pinLoading = false;
    }, 1500);

    let previousZoom = this.map.getZoom();
    this.map.on('zoomend', async () => {
      const currentZoom = this.map.getZoom();

      if (currentZoom >= 10.5 && currentZoom > previousZoom + 0.1) {
        const bounds = this.map.getBounds();

        if (bounds && !this.isViewportLoaded(bounds)) {
          this.pinLoading = true;
          this.saveLoadedViewport(bounds);
          this.getPinsByBounds(bounds);
        } else {
          console.log('In the viewport');
        }
      }

      previousZoom = currentZoom;
    });

    // this.map.moveLayer('point-labels');
    // this.map.moveLayer('points');

  }

  async getMyLocation(): Promise<[number, number]> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser.');
        resolve([-74.5, 40]); // fallback location
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve([position.coords.longitude, position.coords.latitude]);
        },
        (error) => {
          console.error('Browser geolocation error:', error);
          resolve([-74.5, 40]); // fallback location
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  async setLiveTracking() {
    if (this.liveTracking) {
      this.liveTracking = false;
      this.map.removeControl(this.tracking)
    } else {

      this.map.addControl(this.tracking);

      setTimeout(() => {

        this.tracking.trigger();

      }, 200);

      this.liveTracking = true;
    }
  }

  handleAreaClick(e: any) {
    if (this.areaSelection) {

      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['my-polygon-fill']
      });

      if (features.length) {

        let selectedFeature: any;

        if (features.length === 1) {
          selectedFeature = features[0];
        } else {
          selectedFeature = features.sort((a, b) => turf.area(a) - turf.area(b))[0];
        }

        if (selectedFeature?.properties?.['areaId']) {
          let loadingId = this.message.loading('Getting area details...', { nzDuration: 0 }).messageId;
          this.spinner = true;
          this.data.getData(`pins/area-details/${selectedFeature?.properties?.['areaId']}/${this.filterForm.get('provider')?.value}`)
            .subscribe({
              next: async (res: any) => {

                if (res) {
                  setTimeout(() => {
                    this.message.remove(loadingId);
                  }, 500)

                  const modal = this.modal.create({
                    nzContent: AreaModalComponent,
                    nzClosable: false,
                    nzMaskClosable: false,
                    nzFooter: null,
                    nzData: {
                      data: res,
                      usersList: this.users,
                      dispositions: this.dispositions,
                      provider: this.filterForm.get('provider')?.value
                    }
                  })

                  modal.afterClose
                    .subscribe((res: any) => {

                      if (res?.updated) {
                        loadingId = this.message.loading('We’re updating your area — this could take a bit!', { nzDuration: 0 }).messageId;
                        this.updateArea(selectedFeature, res, loadingId);
                      }

                      if (res?.pins) {
                        this.spinner = true;
                        const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;
                        const currentData = source._data as GeoJSON.FeatureCollection;

                        const updatedFeatures = currentData.features.map((feature) => {
                          if (feature.properties?.['id'] && res?.pins.includes(feature.properties['id'])) {
                            return {
                              ...feature,
                              properties: {
                                ...feature.properties,
                                color: res?.pinColor || '#000'
                              }
                            };
                          }
                          return feature;
                        });

                        const updatedGeojson: GeoJSON.FeatureCollection = {
                          ...currentData,
                          features: updatedFeatures
                        };

                        source.setData(updatedGeojson);
                        this.turnOffSpinner();
                      }

                      if (res?.delete) {
                        loadingId = this.message.loading('Deleting area — this may take a moment.', { nzDuration: 0 }).messageId;
                        this.deleteArea(selectedFeature, loadingId);
                      }

                    })
                }
              }
            })
        }
      }
    }
  }

  areaClick() {
    this.map.off('click', 'my-polygon-fill', this.handleAreaClick);
    this.map.on('click', 'my-polygon-fill', this.handleAreaClick);
  }

  async deleteArea(selectedFeature: any, loadingId: any) {
    this.areasRes = this.areasRes.filter((a: any) => a.id != selectedFeature?.properties?.['areaId']);

    this.data.postData({ id: selectedFeature?.properties?.['areaId'] }, `area/delete`)
      .subscribe({
        next: async (res: any) => {
          const source = this.map.getSource('my-polygon') as mapboxgl.GeoJSONSource;
          const currentData = source._data as GeoJSON.FeatureCollection;

          const areaIdToDelete = selectedFeature.properties.areaId;

          const updatedFeatures = currentData.features.filter(
            (feature: any) => feature.properties.areaId !== areaIdToDelete
          );

          source.setData({
            type: 'FeatureCollection',
            features: updatedFeatures
          });

          setTimeout(() => {
            this.message.remove(loadingId);
          }, 500)
        },
        error: (error: any) => {
          alert('Failed to delete this area');
          setTimeout(() => {
            this.turnOffSpinner();
          }, 1000);
        }
      })
  }

  updateArea(selectedFeature: any, data: any, loadingId: any) {
    let clickedIndex = this.areasRes.findIndex((a: any) => a.id == selectedFeature?.properties?.['areaId']);
    this.areasRes = this.areasRes.map((area: any, i: any) => i === clickedIndex ? { ...area, color: data?.color } : area);
    data.users = data.users?.map((user: any) => ({
      ...user,
      id: user?.id || user.user_id
    })) || [];

    this.data.postData({ id: selectedFeature?.properties?.['areaId'], users: data.users ? data.users : [], color: data?.color, provider: this.filterForm.get('provider')?.value }, `area/update`)
      .subscribe({
        next: (res: any) => {
          const source = this.map.getSource(selectedFeature?.source) as mapboxgl.GeoJSONSource;
          const currentData = source._data as GeoJSON.FeatureCollection;

          const updatedFeatures = currentData.features.map((feature: any) => {
            if (feature.properties.areaId == selectedFeature?.properties?.['areaId']) {
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  color: data?.color
                }
              };
            }
            return feature;
          });

          source.setData({
            type: 'FeatureCollection',
            features: updatedFeatures
          });

          setTimeout(() => {
            this.message.remove(loadingId);
          }, 500)
        }
      })

  }

  setMarkerMode() {
    this.areaSelection = false;
    if (this.drawingMode) {
      this.setMapDrawing();
    }
    if (this.movePins) {
      this.enableMapGestures();
    }
    this.movePins = false;
    if (this.markerMode) {
      this.markerMode = false;
    } else {
      this.markerMode = true;
    }
  }

  async setMapDrawing() {
    this.areaSelection = false;
    this.markerMode = false;
    if (this.drawingMode) {
      this.drawingMode = false;
      await this.draw.deleteAll();
      this.map.removeControl(this.draw as any)
    } else {
      this.drawingMode = true;
      this.map.addControl(this.draw as any);
      this.draw.changeMode('draw_polygon');
      this.areaDrawingListener();
      this.map.on('draw.modechange', (e: any) => {
        if (this.drawingMode) {
          this.draw.changeMode('draw_polygon');
        }
      });
    }
  }

  setAreaSelection() {
    this.markerMode = false;
    if (this.drawingMode) {
      this.setMapDrawing();
    }
    if (this.movePins) {
      this.enableMapGestures();
    }
    this.movePins = false;
    if (this.areaSelection) {
      this.areaSelection = false;
    } else {
      this.areaSelection = true;
    }
  }

  areaDrawingListener() {
    this.map.once('draw.create', async (e: mapboxgl.MapboxEvent & { features: GeoJSON.Feature[] }) => {
      const polygon = e.features[0];

      const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource | undefined;
      if (!source) return;

      const data = source._data as GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
      if (!data?.features) return;

      const features = data.features;

      const pointsInside: { id: string; color: string, disposition_id: string } [] = [];
      const seenIds = new Set<string>();

      for (const f of features) {
        // Skip clusters
        if (f.properties?.['point_count']) continue;

        // Only Point geometries
        if (f.geometry.type !== 'Point') continue;


        const featureId = f.properties?.['id']?.toString();
        if (!featureId || seenIds.has(featureId)) continue; // skip duplicates
        seenIds.add(featureId);

        const polyFeature: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> = {
          type: 'Feature',
          geometry: polygon.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
          properties: {},
        };

        if (booleanPointInPolygon(point(f.geometry.coordinates), polyFeature)) {

          pointsInside.push({
            id: featureId,
            color: f.properties?.['color']?.trim() || '#888888',
            disposition_id: f.properties?.['disposition_id']
          });
        }

      }
      this.callAreaActionSheet(polygon, pointsInside);
    });
  }

  async callAreaActionSheet(polygon: any, pinsInside: any) {
    this.showDrawer = true;
    this.newPolygon = polygon;
    this.pinsInside = pinsInside;

    this.drawerTitle = `${pinsInside?.length ? pinsInside?.length : 0} pin(s) selected`;
  }

  assignAndSave() {
    this.showDrawer = false;
    this.newAreaForm.reset()
    this.showCreateAreaModal = true;
    const noDispo = this.pinsInside?.filter((pin: any) => !pin?.disposition_id)?.length || 0;
    this.newAreaStats = this.dispositions?.map((d: any) => {
      return {
        id: d?.id,
        disposition: d?.disposition,
        color: d?.pin,
        count: this.pinsInside?.filter((pin: any) => pin.disposition_id == d.id)?.length || 0
      }
    })

    if(noDispo > 0) {
      this.newAreaStats.push({
        id: null,
        disposition: 'No Disposition',
        color: '#808080',
        count: noDispo
      });
    }

    this.selectedDispos = this.newAreaStats?.map((d: any) => d?.id)
    this.selectedDisposCount = this.pinsInside?.length || 0

    this.newAreaStats = this.newAreaStats?.filter((stats: any) => stats?.count > 0)
    setTimeout(() => this.setUpChart(), 200);
  }

  subSegmentChange(event: any) {
    this.subSegment = event;
  }

  sortArr(data: any) {
    return data.sort((a: any, b: any) => b.count - a.count);
  }

  handleDispoClicked(disp: any) {
    if(this.selectedDispos?.includes(disp?.id)) {
      const index = this.selectedDispos?.indexOf(disp?.id);
      this.selectedDispos.splice(index, 1);
      this.selectedDisposCount -= this.newAreaStats?.find((pin: any) => pin?.id == disp?.id)?.count || 0
    }else {
      this.selectedDispos.push(disp?.id);
      this.selectedDisposCount += this.newAreaStats?.find((pin: any) => pin?.id == disp?.id)?.count || 0
    }
  }

  setUpChart() {
    const ctx = this.pieChartAdd.nativeElement.getContext('2d');
  
    const labels = this.newAreaStats?.map((item: any) =>
      item.disposition ? item.disposition : 'No disposition'
    );
    const data = this.newAreaStats?.map((item: any) => parseInt(item.count, 10));
    const backgroundColors = this.newAreaStats?.map((item: any) => item.color || '#ccc');
  
    new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          label: 'Disposition Counts',
          data: data,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors,
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        animation: {
          duration: 1500,
          easing: 'easeOutQuart',
          animateRotate: true,
          animateScale: true,
        },
        plugins: {
          legend: {
            display: false
          },
          datalabels: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context: any) {
                const label = context.label || '';
                const value = context.raw;
                return `${label}: ${value}`;
              }
            }
          }
        }
      }
    });
  }

  cancelDrawing() {
    this.draw.delete(this.newPolygon.id);
    this.map.removeControl(this.draw as any);
    this.drawingMode = false;
    this.showDrawer = false;
    this.newPolygon = false;
  }

  saveNewArea() {
    if (this.newAreaForm.get('users')?.value?.length > 0) {
      this.showCreateAreaModal = false;

      let obj = {
        users: this.newAreaForm.get('users')?.value,
        color: this.newAreaForm.get('color')?.value,
        polygon: this.newPolygon.geometry?.coordinates?.[0],
        pins: this.pinsInside?.filter((p: any) => this.selectedDispos.includes(p?.disposition_id)),
        provider: this.filterForm.get('provider')?.value
      }

      let loadingId = this.message.loading('Creating your area — this might take a little while depending on size.', { nzDuration: 0 }).messageId;
      this.data.postData(obj, 'pins/save-area-web')
        .subscribe({
          next: async (res: any) => {
            const feature = this.draw.get(this.newPolygon.id);

            setTimeout(() => {
              this.message.remove(loadingId);
            }, 500)

            if (feature) {
              const source = this.map.getSource('my-polygon') as mapboxgl.GeoJSONSource;

              const currentData = source._data as GeoJSON.FeatureCollection;

              const newPolygon = {
                ...feature,
                properties: {
                  ...feature.properties,
                  color: this.newAreaForm.get('color')?.value || '#000',
                  areaId: res?.data
                }
              };

              const obj = {
                id: res?.data,
                color: this.newAreaForm.get('color')?.value || '#000',
                coords: JSON.stringify(this.newPolygon.geometry?.coordinates?.[0])
              }

              this.areasRes.push(obj)

              const updatedFeatures = [...currentData.features, newPolygon];

              source.setData({
                type: 'FeatureCollection',
                features: updatedFeatures
              });

              this.setMapDrawing();

            }
          }
        })
    } else {
      alert('User(s) are required to create the area.')
    }
  }

  pinClick() {
    this.map.off('click', 'points', this.handlePinClick);
    this.map.on('click', 'points', this.handlePinClick);
  }

  handleMduClick(id: any) {
    this.data.getData(`pins/mdu-pins?id=${id}&provider=${this.filterForm.get('provider')?.value}`)
      .subscribe({
        next: async (res: any) => {

          const modal = this.modal.create({
            nzContent: MduListComponent,
            nzClosable: true,
            nzMaskClosable: false,
            nzFooter: null,
            nzTitle: res?.data?.[0]?.address_line_1 ?  `${res?.data?.[0]?.address_line_1} ${res?.data?.[0]?.address_city}, ${res?.data?.[0]?.address_state} ${res?.data?.[0]?.address_zip}` : 'MDU',
            nzData: {
              data: res?.data,
              company: this.filterForm.get('company')?.value || this.currentUser?.company_id,
              provider: this.filterForm.get('provider')?.value,
              dispositions: this.dispositions,
            }
          })

          // const modal = await this.modalCtrl.create({
          //   component: MduListComponent,
          //   componentProps: { 
          //     mdus: res?.data ,
          //     currentUser: this.currentUser,
          //     dispositions: this.dispositions,
          //     selectedProvider: this.selectedProvider,
          //     selectedProviderName: this.selectedProviderName,
          //     lat: res?.data[0]?.latitude,
          //     lng: res?.data[0]?.longitude
          //   },
          //   breakpoints: [0, 0.25, 0.5, 0.75, 1], 
          //   initialBreakpoint: initialBreak,
          // });

          // await modal.present();
          
          this.turnOffSpinner();
        },
        error: (err: any) => {
          // console.log(err)
          // this.turnOffSpinner();
          // this.toasty('Failed to load MDU units');
        }
      });
  }

  handlePinClick(e: any) {
    if (!this.areaSelection && e.features?.length && !this.movePins) {
      const pinFeature = e.features[0];

      const id = pinFeature.properties?.['id'];

      let clickedIndex = this.pins.findIndex((p: any) => p.id == id)

      if(pinFeature?.properties?.['mdu']) {

        let loadingId = this.message.loading('Getting mdu details..', { nzDuration: 0 }).messageId;
        this.handleMduClick(id)

        setTimeout(() => {
          this.message.remove(loadingId);
        }, 500)

      }else if (id) {

        let loadingId = this.message.loading('Getting pin details..', { nzDuration: 0 }).messageId;

        this.data.getData(`pins/pin-data/${id}/${this.filterForm.get('provider')?.value}/${this.filterForm.get('company')?.value}`)
          .subscribe({
            next: async (res: any) => {
              res.id = id

              setTimeout(() => {
                this.message.remove(loadingId);
              }, 250)

              const modal = this.modal.create({
                nzContent: PinModalComponent,
                nzClosable: false,
                nzMaskClosable: false,
                nzFooter: null,
                nzData: {
                  data: res,
                  coords: {
                    latitude: e.lngLat.lat,
                    longitude: e.lngLat.lng,
                  },
                  dispositions: this.dispositions
                }
              })

              modal.afterClose
                .subscribe((res: any) => {

                  if (res) {
                    loadingId = this.message.loading('Updating pin..', { nzDuration: 0 }).messageId;

                    this.pins[clickedIndex].color = res?.pin;

                    const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;
                    const currentData = source._data as GeoJSON.FeatureCollection;

                    const updatedFeatures = currentData.features.map((feature) => {
                      if (feature.properties?.['id'] == res.res) {
                        return {
                          ...feature,
                          properties: {
                            ...feature.properties,
                            color: res?.pin
                          }
                        };
                      }
                      return feature;
                    });

                    const updatedGeojson: GeoJSON.FeatureCollection = {
                      ...currentData,
                      features: updatedFeatures
                    };

                    source.setData(updatedGeojson);
                    setTimeout(() => {
                      this.message.remove(loadingId);
                    }, 500)
                  }
                })
            }
          });

      }
    } else if (this.movePins) {

      this.map.on('mousedown', 'points', (e) => {
        if (!this.movePins) return;
        this.movingPinId = e.features?.[0]?.properties?.['id'] ?? null;
        const touch = e?.point

        if (!touch) return;

        const lngLat = this.map.unproject([touch.x, touch.y]);

        this.startLatandLng = lngLat;
      });

      let raf = false;

      this.map.on('mousemove', (e) => {
        if (!this.movePins || !this.movingPinId || raf) return;
        raf = true;
        const touch = e?.point;

        if (!touch) return;

        const lngLat = this.map.unproject([touch.x, touch.y]);
        const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;
        const data = source._data as GeoJSON.FeatureCollection;
        const feature = data?.features.find((f: any) => f?.properties?.id === this.movingPinId);

        if (!feature) {
          raf = false;
          return;
        }

        (feature.geometry as GeoJSON.Point).coordinates = [
          lngLat.lng,
          lngLat.lat
        ];

        this.lastLatandLng = lngLat;

        source.setData(data);

        raf = false;
      });

      this.map.on('mouseup', () => {
        if (!this.movePins || !this.movingPinId || !this.lastLatandLng) return;

        const payload = {
          id: this.movingPinId,
          lng: this.lastLatandLng.lng,
          lat: this.lastLatandLng.lat
        };


        if (confirm("Do you want to update the location of this pin?")) {
          this.pinLoading = true;

          try {
            this.data.postData(payload, `pins/moved-pin`)
              .subscribe({
                next: (res: any) => {
                  this.pinLoading = false;
                  alert('Pin location updated!')
                },
                error: (err: any) => {
                  console.log(err)
                  alert('failed to update pin location!');
                  this.pinLoading = false;
                }
              })
          } catch (error) {
            alert('Unable to update pin location!');
            this.pinLoading = false;
          }


        } else {

          const source = this.map.getSource('locations') as mapboxgl.GeoJSONSource;
          const data = source._data as GeoJSON.FeatureCollection;
          const feature = data?.features.find((f: any) => f?.properties?.id === this.movingPinId);

          if (!feature) {
            return;
          }

          (feature.geometry as GeoJSON.Point).coordinates = [
            this.startLatandLng.lng,
            this.startLatandLng.lat
          ];
          source.setData(data);

        }

        this.movePins = false;
        this.enableMapGestures();
        this.movingPinId = null;
        this.lastLatandLng = null;
      });

    }
  }

  resetFilter() {
    this.updateNotYouPeenz();
    const prov = localStorage.getItem('provider') || null
    if(prov) {
      this.filterForm.get('provider')?.setValue(prov);
    }else {
      this.filterForm.get('provider')?.setValue(this.currentUser.providers?.[0]?.id);
    }
    this.filterForm.get('user')?.setValue(null)
    this.filterForm.get('company')?.setValue(null)
    this.filterForm.get('disposition')?.setValue(this.dispositions?.map((d: any) => d.id))
    this.pinLoading = true;
    this.getAreas()
    this.showFilterDrawer = false;
    this.loadedAreas = [];
  }

  compareUser = (u1: any, u2: any): boolean => {
    return u1 && u2 ? u1.id === u2.id : u1 === u2;
  };

}
